import React, { useEffect, useState } from 'react';
import { ownerPortal } from '../services/api';
import toast from 'react-hot-toast';

const CATEGORY_ICONS = {
  bylaw: '📋',
  minutes: '📝',
  financial: '💰',
  insurance: '🛡️',
  contract: '📄',
  warranty: '✅',
  correspondence: '✉️',
  form_b: '📜',
  form_f: '🧾',
  other: '📁',
};

const CATEGORY_COLORS = {
  bylaw: 'bg-purple-100 text-purple-700',
  minutes: 'bg-blue-100 text-blue-700',
  financial: 'bg-green-100 text-green-700',
  insurance: 'bg-orange-100 text-orange-700',
  contract: 'bg-gray-100 text-gray-700',
  warranty: 'bg-teal-100 text-teal-700',
  correspondence: 'bg-yellow-100 text-yellow-700',
  form_b: 'bg-indigo-100 text-indigo-700',
  form_f: 'bg-teal-100 text-teal-700',
  other: 'bg-gray-100 text-gray-600',
};

const CATEGORY_LABELS = { form_b: 'Form B', form_f: 'Form F' };

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OwnerDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    ownerPortal.documents()
      .then((res) => setDocs(res.data))
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await ownerPortal.downloadDocument(doc.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const categories = [...new Set(docs.map((d) => d.category))];
  const filtered = docs.filter((d) => {
    const matchesSearch = !search ||
      d.name.toLowerCase().includes(search.toLowerCase());
    const matchesCat = !filterCat || d.category === filterCat;
    return matchesSearch && matchesCat;
  });

  // Group by category
  const grouped = filtered.reduce((acc, doc) => {
    const cat = doc.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Building Documents</h1>
        <p className="text-gray-500 text-sm mt-1">Access bylaws, meeting minutes, and forms shared by your strata manager.</p>
      </div>

      {/* Search & filter */}
      {docs.length > 0 && (
        <div className="flex gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents…"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none" />
          </div>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 outline-none bg-white">
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || (c.charAt(0).toUpperCase() + c.slice(1))}</option>
            ))}
          </select>
        </div>
      )}

      {docs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500">No documents shared yet</p>
          <p className="text-gray-400 text-sm mt-1">Your strata manager will share documents here.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-10 text-center text-gray-400">
          No documents match your search.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, catDocs]) => (
            <div key={cat} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <span className="text-lg">{CATEGORY_ICONS[cat] || '📁'}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-600'}`}>
                  {CATEGORY_LABELS[cat] || (cat.charAt(0).toUpperCase() + cat.slice(1))}
                </span>
                <span className="text-gray-400 text-xs ml-auto">{catDocs.length} file{catDocs.length !== 1 ? 's' : ''}</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {catDocs.map((doc) => (
                  <li key={doc.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatSize(doc.file_size)}
                        {doc.file_size ? ' · ' : ''}
                        {new Date(doc.created_at).toLocaleDateString('en-CA', {
                          year: 'numeric', month: 'short', day: 'numeric'
                        })}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDownload(doc)}
                      disabled={downloading === doc.id}
                      className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50 shrink-0">
                      {downloading === doc.id ? (
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      )}
                      Download
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
